<script setup lang="ts">
/**
 * Incorporation (Pre) — M32100 스튜디오 패키지 (표준 MY Stage 스텝퍼).
 *   Stage 1: Section 14 (IncorporationDoc, hideSection201) — 자체 페이지 렌더
 *   Stage 2: CDD — 사람/클라이언트별 인스턴스 문서-리스트
 *   Stage 3: Section 201 — 선임 이사별 인스턴스(Declaration 기본 + Add Declaration/Attestation)
 */
import { ref, computed, inject } from 'vue';
import IncorporationDoc from '../incorporation/IncorporationDoc.vue';
import CddIndividualDoc from '../cdd/CddIndividualDoc.vue';
import CddLegalPersonDoc from '../cdd/CddLegalPersonDoc.vue';
import CddLegalArrangementDoc from '../cdd/CddLegalArrangementDoc.vue';
import Section201Doc from './Section201Doc.vue';
import { INC_PRE_CDD, CDD_TYPE_META } from './incPreCddContext';
import { INC_PRE_S201, S201_TYPE_META } from './incPreS201Context';
import type { CddDocType } from '../cdd/cddFields';
import type { S201DocType } from './incPreS201Context';

const cdd = inject(INC_PRE_CDD, null);
const s201 = inject(INC_PRE_S201, null);
const activeCddDoc = computed(() => cdd?.docs.value.find((d) => d.id === cdd.activeId.value) || null);
const activeS201Doc = computed(() => s201?.docs.value.find((d) => d.id === s201.activeId.value) || null);
const CDD_ADD_TYPES: CddDocType[] = ['individual', 'legal_person', 'legal_arrangement'];
const S201_ADD_TYPES: S201DocType[] = ['declaration', 'attestation'];
const cddAddMenu = ref(false);
const s201AddMenu = ref(false);
function cddName(d: { docType: CddDocType; values: Record<string, string> }): string {
  const k = d.docType === 'individual' ? 'ind_full_name' : d.docType === 'legal_person' ? 'lp_company_name' : 'la_name';
  return String(d.values[k] || '').trim();
}

const currentStage = ref(1);
const TOTAL = 3;
function go(n: number) { currentStage.value = n; }
function goRel(d: number) { const n = currentStage.value + d; if (n >= 1 && n <= TOTAL) currentStage.value = n; }

const incDocRef = ref<InstanceType<typeof IncorporationDoc> | null>(null);
function gatherLocalState() { return incDocRef.value?.gatherLocalState?.() || { directorRows: [1], memberRows: [1] }; }
function restoreLocalState(s: any) { incDocRef.value?.restoreLocalState?.(s); }
function relayoutPages() { incDocRef.value?.relayoutPages?.(); }
defineExpose({ gatherLocalState, restoreLocalState, relayoutPages });
</script>

<template>
  <div class="allot-pkg">
    <div class="stepper">
      <button class="step" :class="{ active: currentStage === 1, done: currentStage > 1 }" @click="go(1)">
        <span class="step-no">{{ currentStage > 1 ? '✓' : 1 }}</span>
        <span class="step-txt"><b>Stage 1</b><small>Section 14</small></span>
      </button>
      <button class="step" :class="{ active: currentStage === 2, done: currentStage > 2 }" @click="go(2)">
        <span class="step-no">{{ currentStage > 2 ? '✓' : 2 }}</span>
        <span class="step-txt"><b>Stage 2</b><small>CDD ({{ cdd?.docs.value.length || 0 }})</small></span>
      </button>
      <button class="step" :class="{ active: currentStage === 3 }" @click="go(3)">
        <span class="step-no">3</span>
        <span class="step-txt"><b>Stage 3</b><small>Section 201 ({{ s201?.docs.value.length || 0 }})</small></span>
      </button>
    </div>

    <!-- Stage 1 · Section 14 -->
    <section v-show="currentStage === 1" class="stage">
      <div class="stage-head"><span class="stage-badge">Stage 1</span><span class="stage-t">Section 14 — Particulars of Company</span></div>
      <div class="pkg-doc"><IncorporationDoc ref="incDocRef" :hide-section201="true" /></div>
    </section>

    <!-- Stage 2 · CDD 인스턴스 리스트 -->
    <section v-show="currentStage === 2" class="stage">
      <div class="stage-head"><span class="stage-badge">Stage 2</span><span class="stage-t">Client Due Diligence — per client</span></div>
      <div class="doc-tabs">
        <button
          v-for="d in (cdd?.docs.value || [])" :key="d.id"
          class="doc-tab" :class="{ active: cdd?.activeId.value === d.id }" @click="cdd?.select(d.id)"
        >
          <v-icon size="15">{{ CDD_TYPE_META[d.docType].icon }}</v-icon>
          <span class="tab-nm">{{ cddName(d) || CDD_TYPE_META[d.docType].label }}</span>
          <span class="tab-ty">{{ CDD_TYPE_META[d.docType].label }}</span>
          <v-icon size="14" class="tab-x" @click.stop="cdd?.remove(d.id)">mdi-close</v-icon>
        </button>
        <v-menu v-model="cddAddMenu" location="bottom start">
          <template #activator="{ props: mp }"><button v-bind="mp" class="doc-add"><v-icon size="16">mdi-plus</v-icon> Add CDD</button></template>
          <v-list density="compact">
            <v-list-item v-for="t in CDD_ADD_TYPES" :key="t" :prepend-icon="CDD_TYPE_META[t].icon" :title="CDD_TYPE_META[t].label" @click="cdd?.add(t); cddAddMenu = false" />
          </v-list>
        </v-menu>
      </div>
      <div v-if="!activeCddDoc" class="doc-empty">No CDD documents. Click <b>Add CDD</b> to add one per client.</div>
      <div v-else class="pkg-doc">
        <div class="pkg-paper">
          <CddIndividualDoc v-if="activeCddDoc.docType === 'individual'" :key="'i' + activeCddDoc.id" />
          <CddLegalPersonDoc v-else-if="activeCddDoc.docType === 'legal_person'" :key="'p' + activeCddDoc.id" />
          <CddLegalArrangementDoc v-else :key="'a' + activeCddDoc.id" />
        </div>
      </div>
    </section>

    <!-- Stage 3 · Section 201 인스턴스 리스트 -->
    <section v-show="currentStage === 3" class="stage">
      <div class="stage-head"><span class="stage-badge">Stage 3</span><span class="stage-t">Section 201 — per appointed director</span></div>
      <div class="doc-tabs">
        <button
          v-for="d in (s201?.docs.value || [])" :key="d.id"
          class="doc-tab" :class="{ active: s201?.activeId.value === d.id }" @click="s201?.select(d.id)"
        >
          <v-icon size="15">{{ S201_TYPE_META[d.docType].icon }}</v-icon>
          <span class="tab-nm">{{ (d.values.s201_name || '').trim() || 'Director' }}</span>
          <span class="tab-ty">{{ d.docType === 'attestation' ? 'Attestation' : 'Declaration' }}</span>
          <v-icon size="14" class="tab-x" @click.stop="s201?.remove(d.id)">mdi-close</v-icon>
        </button>
        <v-menu v-model="s201AddMenu" location="bottom start">
          <template #activator="{ props: mp }"><button v-bind="mp" class="doc-add"><v-icon size="16">mdi-plus</v-icon> Add Section 201</button></template>
          <v-list density="compact">
            <v-list-item v-for="t in S201_ADD_TYPES" :key="t" :prepend-icon="S201_TYPE_META[t].icon" :title="S201_TYPE_META[t].label" @click="s201?.add(t); s201AddMenu = false" />
          </v-list>
        </v-menu>
      </div>
      <div v-if="!activeS201Doc" class="doc-empty">No Section 201 documents. Click <b>Add Section 201</b> to add one per appointed director.</div>
      <div v-else class="pkg-doc">
        <div class="pkg-paper">
          <Section201Doc :key="'s' + activeS201Doc.id" :with-attestation="activeS201Doc.docType === 'attestation'" />
        </div>
      </div>
    </section>

    <div class="stage-nav">
      <button v-if="currentStage > 1" class="nav-btn" @click="goRel(-1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ TOTAL }}</span>
      <button v-if="currentStage < TOTAL" class="nav-btn primary" @click="goRel(1)">Next →</button>
      <button v-else class="nav-btn primary" disabled>All stages ✓</button>
    </div>
  </div>
</template>

<style scoped>
.allot-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 100%; max-width: 900px; margin: 0 auto 18px; padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, .06); }
.step { flex: 1; display: flex; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all .15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stage { width: 100%; max-width: 860px; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }
.doc-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; align-items: center; }
.doc-tab { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #e3e7ee; border-radius: 999px; background: #fff; cursor: pointer; font-size: 12px; color: #42506a; }
.doc-tab.active { background: #1b64da; border-color: #1b64da; color: #fff; }
.doc-tab .tab-nm { font-weight: 700; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.doc-tab .tab-ty { font-size: 10.5px; opacity: .7; }
.doc-tab .tab-x { opacity: .6; }
.doc-tab .tab-x:hover { opacity: 1; }
.doc-add { display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; border: 1px dashed #b9d0ff; border-radius: 999px; background: #f6f9ff; color: #1b64da; font-weight: 800; font-size: 12px; cursor: pointer; }
.doc-empty { font-size: 12.5px; color: #6b7280; padding: 16px; border: 1px dashed #d5dae4; border-radius: 10px; text-align: center; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-paper { background: #fff; max-width: 820px; margin: 0 auto; padding: 34px 38px; box-shadow: 0 4px 24px rgba(20, 30, 60, .12); font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 860px; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
.pkg-paper :deep(.cddf-combo--share) { min-width: 0; max-width: 100%; width: 100%; }
.pkg-paper :deep(.cddf-combo--share .v-select__selection-text) { overflow: hidden; text-overflow: ellipsis; }
</style>
